import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { getCategories, getDashboard, getExpenses, createExpense, updateExpense, deleteExpense, getMonthlySummary, loginUser, registerUser, createCategory } from './utils/api';
import axios from 'axios';
import { forgotPassword, resetPassword } from './utils/api'; // Adjust the folder path if your App.js is in a different location
const MONTH_NAMES = ['','January','February','March','April','May','June','July','August','September','October','November','December'];
const PIE_COLORS  = ['#f97316','#06b6d4','#8b5cf6','#10b981','#f59e0b','#ef4444','#ec4899','#14b8a6'];
const todayStr    = () => new Date().toISOString().slice(0,10);
const fmtMoney    = (n) => '₹' + Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:2});
const fmtDate     = (d) => d ? String(d).slice(0,10) : '';
const pct         = (u,t) => t ? Math.min(100,Math.round((u/t)*100)) : 0;
const getStored   = (k) => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } };

// ── Toast Component ───────────────────────────────────────
const Toast = ({msg,type,onClose}) => (
  <div className={`toast toast-${type}`}><span>{msg}</span><button onClick={onClose}>✕</button></div>
);

// ── Badge Component ───────────────────────────────────────
const Badge = ({type}) => (
  <span className={`badge ${type==='food'?'badge-food':'badge-nonfood'}`}>{type==='food'?'🍽 Food':'📦 Non-Food'}</span>
);

// 💡 Add this quick payment method formatter badge at the top or inside your component
const renderPaymentBadge = (method) => {
  switch (method) {
    case 'meal_card': 
      return <span className="pay-chip meal">🍱 Meal Card</span>;
    case 'debit_card': 
      return <span className="pay-chip debit">💳 Axis Debit</span>;
    case 'digital_upi':
    case 'gpay':
    case 'phonepe':
      return <span className="pay-chip upi">📱 UPI Channel</span>;
    default: 
      return <span className="pay-chip cash">💵 Cash Ledger</span>;
  }
};
// ══════════════════════════════════════════════════════════
// AUTHENTICATION PAGE
// ══════════════════════════════════════════════════════════

function AuthPage({ onAuth }) {
  const [tab, setTab] = useState('login'); // 'login', 'register', 'forgot', 'reset'
 //   const [form, setForm] = useState({ name: '', email: '', password: '', monthly_budget: 14000, otp: '', newPassword: '' });
	const [form, setForm] = useState({ name: '', email: '', password: '', monthly_budget: 0, otp: '', newPassword: '' });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleAuthSubmit = async () => {
    setError(''); setMessage(''); setLoading(true);
    try {
      const fn = tab === 'login' ? loginUser : registerUser;
      const payload = tab === 'login'
        ? { email: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password, monthly_budget: form.monthly_budget };

      const res = await fn(payload);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      onAuth(res.data.user);
    } catch (e) {
      setError(e.response?.data?.message || 'Authentication error occurred');
    }
    setLoading(false);
  };

 const handleForgotPassword = async (e) => {
  e.preventDefault();
  try {
    const response = await forgotPassword({ email: form.email });
    
    // Only alter screen layout if the server confirms success
    if (response.data.success) {
      setMessage("Verification code sent to your email inbox!");
      setTab('reset'); // Switch to OTP inputs safely
    }
  } catch (error) {
    // Catch server failures cleanly
    const errorMsg = error.response?.data?.message || "Failed to send recovery email.";
    setMessage(`❌ Error: ${errorMsg}`);
  }
};

  const handleResetPassword = async () => {
    if (!form.otp || !form.newPassword) { setError('Please enter both the OTP code and your new password.'); return; }
    setError(''); setMessage(''); setLoading(true);
    try {
      // ✅ FIX: Uses the absolute-path API helper function
      const res = await resetPassword({
        email: form.email,
        reset_token: form.otp,
        newPassword: form.newPassword
      });
      setMessage(res.data.message || 'Password updated successfully! Sign in to continue.');
      setTab('login');
      setForm(f => ({ ...f, password: '', otp: '', newPassword: '' }));
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to finish password reset execution');
    }
    setLoading(false);
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        <div className="auth-brand">
          <div style={{ fontSize: 48 }}>💸</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginTop: 10 }}>Daily Expense Tracker</h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 6 }}>Track your personalized budget ledger matrix</p>
        </div>

        {tab !== 'forgot' && tab !== 'reset' ? (
          <div className="auth-tabs">
            <button className={`auth-tab ${tab === 'login' ? 'active' : ''}`} onClick={() => { setTab('login'); setError(''); setMessage(''); }}>Sign In</button>
            <button className={`auth-tab ${tab === 'register' ? 'active' : ''}`} onClick={() => { setTab('register'); setError(''); setMessage(''); }}>Register</button>
          </div>
        ) : (
          <h3 style={{ textAlign: 'center', marginBottom: 16, color: '#f97316' }}>
            {tab === 'forgot' ? '🔑 Account Security Recovery' : '🔒 Update Account Password'}
          </h3>
        )}

        {error && <div className="auth-error">⚠️ {error}</div>}
        {message && <div style={{ color: '#10b981', backgroundColor: '#064e3b', padding: 10, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>✅ {message}</div>}

        {tab === 'register' && (
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>👤 Full Name</label>
            <input type="text" placeholder="Your name" value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
        )}

        {/* Read-only tracking label during active code submission validation processing loops */}
        <div className="form-group" style={{ marginBottom: 14 }}>
          <label>📧 Email Address</label>
          <input type="email" placeholder="you@example.com" value={form.email} disabled={tab === 'reset'} onChange={e => set('email', e.target.value)} />
        </div>

        {tab === 'reset' && (
          <>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>🔢 Enter 6-Digit Verification Code (OTP)</label>
              <input type="text" placeholder="e.g. 225508" maxLength={6} style={{ letterSpacing: '4px', fontSize: '16px', fontWeight: 'bold', textAlign: 'center' }} value={form.otp} onChange={e => set('otp', e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>🔒 Enter New Secure Password</label>
              <input type="password" placeholder="Min 6 characters" value={form.newPassword} onChange={e => set('newPassword', e.target.value)} />
            </div>
          </>
        )}

        {tab !== 'forgot' && tab !== 'reset' && (
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>🔒 Password</label>
            <input type="password" placeholder="Min 6 characters" value={form.password} onChange={e => set('password', e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAuthSubmit()} />
          </div>
        )}

        {tab === 'register' && (
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>💰 Monthly Budget (₹)</label>
            <input type="number" value={form.monthly_budget} onChange={e => set('monthly_budget', e.target.value)} />
          </div>
        )}

        {tab !== 'forgot' && tab !== 'reset' && (
          <div style={{ textAlign: 'right', marginBottom: 14 }}>
            <button type="button" style={{ background: 'none', border: 'none', color: '#f97316', cursor: 'pointer', fontSize: 12, fontWeight: 500 }} onClick={() => { setTab('forgot'); setError(''); setMessage(''); }}>
              Forgot Password?
            </button>
          </div>
        )}

        {tab === 'forgot' && (
          <>
            <button className="btn btn-primary full-btn" onClick={handleForgotPassword} disabled={loading}>
              {loading ? 'Sending Request…' : 'Send Reset Link Mail →'}
            </button>
            <button className="btn btn-ghost full-btn" style={{ marginTop: 8 }} onClick={() => { setTab('login'); setError(''); setMessage(''); }}>
              Back to Sign In
            </button>
          </>
        )}

        {tab === 'reset' && (
          <>
            <button className="btn btn-primary full-btn" style={{ backgroundColor: '#10b981' }} onClick={handleResetPassword} disabled={loading}>
              {loading ? 'Verifying Code…' : 'Verify Code & Update Password ✓'}
            </button>
            <button className="btn btn-ghost full-btn" style={{ marginTop: 8 }} onClick={() => { setTab('forgot'); setError(''); setMessage(''); }}>
              ← Resend Code
            </button>
          </>
        )}

        {tab !== 'forgot' && tab !== 'reset' && (
          <button className="btn btn-primary full-btn" style={{ marginTop: 8 }} onClick={handleAuthSubmit} disabled={loading}>
            {loading ? 'Processing…' : tab === 'login' ? 'Sign In →' : 'Create Account'}
          </button>
        )}
      </div>
    </div>
  );
}

function BudgetSettingsModal({ currentUser, onBudgetUpdated, onClose }) {
  const [baseBudget, setBaseBudget] = useState(currentUser.monthly_budget || 14000);
  const [topupAmount, setTopupAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', text: '' });

  const handleSaveChanges = async () => {
    setLoading(true);
    setFeedback({ type: '', text: '' });
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put('/api/auth/update-budget', 
        {
          monthly_budget: parseFloat(baseBudget),
          add_topup: topupAmount ? parseFloat(topupAmount) : 0
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setFeedback({ type: 'success', text: 'Budget variables scaled up successfully!' });
        localStorage.setItem('user', JSON.stringify(response.data.user));
        onBudgetUpdated(response.data.user);
        setTopupAmount(''); // Clear topup injection box input field
      }
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.message || 'Failed updating boundaries' });
    }
    setLoading(false);
  };

  return (
    <div style={{ backgroundColor: '#0f172a', padding: '24px', borderRadius: '12px', border: '1px solid #1e293b', maxWidth: '420px', color: '#f8fafc' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        ⚙️ Manage Budget Allocation
      </h2>

      {feedback.text && (
        <div style={{ padding: '10px', borderRadius: '6px', fontSize: '13px', marginBottom: '14px', backgroundColor: feedback.type === 'success' ? '#064e3b' : '#7f1d1d', color: feedback.type === 'success' ? '#10b981' : '#f87171' }}>
          {feedback.type === 'success' ? '✅' : '⚠️'} {feedback.text}
        </div>
      )}

      {/* BLOCK 1: Change Baseline Allocation Rule */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>Base Monthly Budget (₹)</label>
        <input 
          type="number" 
          value={baseBudget} 
          onChange={e => setBaseBudget(e.target.value)}
          style={{ width: '100%', padding: '10px', borderRadius: '6px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#fff' }}
        />
      </div>

      {/* BLOCK 2: Emergency Mid-Month Top-up Injection Form */}
      <div style={{ marginBottom: '20px', padding: '12px', borderRadius: '8px', backgroundColor: '#1e1b4b', border: '1px solid #312e81' }}>
        <label style={{ display: 'block', fontSize: '12px', color: '#c084fc', marginBottom: '6px', fontWeight: 'bold' }}>
          🚨 Overused Budget? Inject Emergency Top-up (₹)
        </label>
        <input 
          type="number" 
          placeholder="e.g. 3000" 
          value={topupAmount} 
          onChange={e => setTopupAmount(e.target.value)}
          style={{ width: '100%', padding: '10px', borderRadius: '6px', backgroundColor: '#0f172a', border: '1px solid #4c1d95', color: '#fff' }}
        />
        <span style={{ fontSize: '11px', color: '#a78bfa', marginTop: '6px', display: 'block' }}>
          Current accumulated top-up additions: ₹{currentUser.budget_topup || 0}
        </span>
      </div>

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#334155', color: '#fff', cursor: 'pointer' }}>
          Cancel
        </button>
        <button onClick={handleSaveChanges} disabled={loading} style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#f97316', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>
          {loading ? 'Processing...' : 'Apply Limits Changes'}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// EXPENSE DIALOG / MODAL 
// ══════════════════════════════════════════════════════════


function ExpenseForm({categories, onSave, onClose, editData, onRefreshCategories}) {
  const [form, setForm] = useState({
    expense_date: todayStr(),
    particulars: '', 
    category_id: '', 
    credit: '', 
    debit: '', 
    remarks: '',
    payment_method: 'cash', // Baseline default state definition
    ...editData,
    expense_date: editData?.expense_date ? fmtDate(editData.expense_date) : todayStr(),
    payment_method: editData?.payment_method || 'cash' // Sync state during edits
  });
  const [saving, setSaving] = useState(false);

  const [showNewCatForm, setShowNewCatForm] = useState(false);
  const [catName, setCatName] = useState('');
  const [catType, setCatType] = useState('non_food');
  const [catIcon, setCatIcon] = useState('📦');
  
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.expense_date || !form.particulars || !form.category_id) { 
      alert('Date, Particulars and Category are required'); 
      return; 
    }
    if (!form.credit && !form.debit) { 
      alert('Enter at least Credit or Debit amount'); 
      return; 
    }
    setSaving(true);
    try {
      const payload = { 
        ...form,
        credit: form.credit === '' ? 0 : parseFloat(form.credit) || 0,
        debit:  form.debit === '' ? 0 : parseFloat(form.debit) || 0,
      };
      if (editData?.id) await updateExpense(editData.id, payload);
      else             await createExpense(payload);
      onSave();
    } catch (e) { 
      alert(e.response?.data?.message || 'Error saving record'); 
    }
    setSaving(false);
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!catName.trim()) { alert('Category Name is required'); return; }
    try {
      const res = await createCategory({ name: catName, type: catType, icon: catIcon });
      if (res.data.success) {
        if (onRefreshCategories) await onRefreshCategories();
        const createdId = res.data.data?.id || res.data.id;
        if (createdId) set('category_id', createdId);
        setCatName('');
        setShowNewCatForm(false);
      } else {
        alert(res.data.message || 'Failed to create category');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Network exception processing custom category addition');
    }
  };

  const foodCats    = categories.filter(c => c.type === 'food');
  const nonFoodCats = categories.filter(c => c.type === 'non_food' || c.type === 'debit' || c.type === 'credit');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editData?.id ? '✏️ Edit Expense' : '➕ Add Expense'}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="form-grid">
          <div className="form-group span2">
            <label>📅 Expense Date</label>
            <input type="date" value={form.expense_date} onChange={e => set('expense_date', e.target.value)} />
          </div>
          <div className="form-group span2">
            <label>📝 Particulars</label>
            <input type="text" placeholder="e.g. Hotel Saravana, Petrol Pump…" value={form.particulars} onChange={e => set('particulars', e.target.value)} />
          </div>

          <div className="form-group span2">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ margin: 0 }}>🏷  Category</label>
              <button type="button" style={{ background: 'none', border: 'none', color: '#f97316', fontSize: 12, cursor: 'pointer', fontWeight: 600 }} onClick={() => setShowNewCatForm(!showNewCatForm)}>
                {showNewCatForm ? '✕ Cancel' : '➕ New Category'}
              </button>
            </div>

            {showNewCatForm ? (
              <div style={{ background: '#1e293b', padding: 12, borderRadius: 6, border: '1px solid #334155', marginTop: 4 }}>
                <span style={{ fontSize: '11px', color: '#f97316', display: 'block', marginBottom: 6 }}>✨ Create New Category Wise</span>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input type="text" placeholder="🍔" value={catIcon} style={{ width: 55, textAlign: 'center' }} onChange={e => setCatIcon(e.target.value)} maxLength={2} />
                  <input type="text" placeholder="Category Name" value={catName} style={{ flex: 1 }} onChange={e => setCatName(e.target.value)} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <select value={catType} style={{ width: '40%', padding: 6 }} onChange={e => setCatType(e.target.value)}>
                    <option value="non_food">📦 Non-Food</option>
                    <option value="food">🍽️  Food</option>
                  </select>
                  <button type="button" className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={handleCreateCategory}>Save</button>
                </div>
              </div>
            ) : (
              <select value={form.category_id} onChange={e => set('category_id', e.target.value)}>
                <option value="">— Select Category —</option>
                <optgroup label="🍽  Food">
                  {foodCats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                </optgroup>
                <optgroup label="📦 Non-Food">
                  {nonFoodCats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                </optgroup>
              </select>
            )}
          </div>

          {/* 🔥 Comprehensive Payment Gateway Selector Selection Dropdown */}
          <div className="form-group span2">
            <label>💳 Paid Via / Financial Channel</label>
            <select value={form.payment_method} onChange={e => set('payment_method', e.target.value)}>
              <optgroup label="📱 Digital UPI Systems">
                <option value="gpay">🟢 Google Pay (GPay)</option>
                <option value="phonepe">🟣 PhonePe</option>
                <option value="paytm">🔵 Paytm</option>
                <option value="upi">⚡ Generic UPI ID / QR</option>
              </optgroup>
              <optgroup label="💳 Credit & Debit Cards">
                <option value="axis_card">🟠 Axis Bank Debit Card</option>
                <option value="hdfc_card">🔵 HDFC Bank Debit Card</option>
                <option value="sbi_card">🟢 SBI Debit Card</option>
                <option value="indian_bank_card">🔵 Indian Bank Debit Card</option>
                <option value="meal_card">🍱 HDFC Meal Card</option>
                <option value="credit_card">💳 General Credit Card</option>
              </optgroup>
              <optgroup label="🏛️ Online NetBanking Channels">
                <option value="net_axis">🏛️ Axis NetBanking</option>
                <option value="net_hdfc">🏛️ HDFC NetBanking</option>
                <option value="net_sbi">🏛️ SBI NetBanking</option>
                <option value="net_ib">🏛️ Indian Bank NetBanking</option>
                <option value="net_banking">🌐 Other NetBanking</option>
              </optgroup>
              <optgroup label="💵 Cash Accounts">
                <option value="cash">💵 Cash Ledger</option>
              </optgroup>
            </select>
          </div>

          <div className="form-group">
            <label>💚 Credit (₹)</label>
            <input type="number" min="0" step="0.01" placeholder="0.00" value={form.credit} onChange={e => set('credit', e.target.value)} />
          </div>
          <div className="form-group">
            <label>❤️ Debit (₹)</label>
            <input type="number" min="0" step="0.01" placeholder="0.00" value={form.debit} onChange={e => set('debit', e.target.value)} />
          </div>
          <div className="form-group span2">
            <label>💬 Remarks</label>
            <textarea rows={2} placeholder="Optional notes…" value={form.remarks} onChange={e => set('remarks', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : (editData?.id ? 'Update' : 'Add Expense')}</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// METRICS DASHBOARD VIEW
// ══════════════════════════════════════════════════════════
function Dashboard({ onAddExpense, user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const load = useCallback(async () => {
    try {
      const r = await getDashboard();
      setData(r.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div className="loading">Loading dashboard…</div>;
  if (!data)   return <div className="loading">Failed to load view details</div>;

  const { monthTotals, todayTotals, topCategories, recentTransactions } = data;

  // 1. 🔥 FIXED MATH: Calculate total combined budget dynamically from live props
  const baseBudget = parseFloat(user?.monthly_budget || 0);
  const topupBudget = parseFloat(user?.budget_topup || 0);
  const totalBudget = baseBudget + topupBudget; // Combined capacity (e.g., 14000 + 16000 = 30000)
// console.log("Total Budget :",totalBudget,baseBudget,topupBudget);
  // 2. 🔥 FIXED UI METRICS: Remapped using the updated totalBudget context
  const budgetUsed = pct(monthTotals.month_debit, totalBudget);
  const budgetLeft = totalBudget - monthTotals.month_debit;

  // Normalized adapter wrapper to fix missing data states inside Dashboard View
  const cleanTopCategories = (topCategories || [])
    .map(c => ({
      name: c.name || c.category_name || 'Other',
      value: parseFloat(c.total || c.total_debit || 0)
    }))
    .filter(c => c.value > 0);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Welcome, {user?.name} 👋</h1>
          <p className="subtitle">{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
        <button className="btn btn-primary" onClick={onAddExpense}>+ Add Expense</button>
      </div>

      <div className="stat-grid">
        {/* 🔥 UPDATED: Displays combined live budget total */}
        <div className="stat-card stat-budget">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <span className="stat-label">Monthly Budget</span>
            <span className="stat-value">{fmtMoney(totalBudget)}</span>
          </div>
        </div>

        <div className="stat-card stat-debit">
          <div className="stat-icon">📤</div>
          <div className="stat-info">
            <span className="stat-label">Month Spent</span>
            <span className="stat-value">{fmtMoney(monthTotals.month_debit)}</span>
          </div>
        </div>

        {/* 🔥 UPDATED: Automatically flips to credit/danger color profiles based on real remaining balance */}
        <div className={`stat-card ${budgetLeft >= 0 ? 'stat-credit' : 'stat-danger'}`}>
          <div className="stat-icon">{budgetLeft >= 0 ? '✅' : '⚠️'}</div>
          <div className="stat-info">
            <span className="stat-label">Balance Left</span>
            <span className="stat-value">{fmtMoney(budgetLeft)}</span>
          </div>
        </div>

        <div className="stat-card stat-today">
          <div className="stat-icon">📅</div>
          <div className="stat-info">
            <span className="stat-label">Today's Spend</span>
            <span className="stat-value">{fmtMoney(todayTotals.today_debit)}</span>
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Budget Utilisation — {new Date().toLocaleString('default', { month: 'long' })} {new Date().getFullYear()}</h3>
        <div className="progress-bar-wrap">
          {/* 🔥 UPDATED: Bar track color shifting matches the fresh combined budget percentage limits */}
          <div className="progress-bar-track">
            <div className={`progress-bar-fill ${budgetUsed > 90 ? 'fill-danger' : budgetUsed > 70 ? 'fill-warn' : 'fill-ok'}`} style={{ width: `${budgetUsed}%` }} />
          </div>
          <span className="progress-label">{budgetUsed}%</span>
        </div>
        <div className="budget-row">
          <span>Spent: {fmtMoney(monthTotals.month_debit)}</span>
          {/* 🔥 UPDATED: Corrected bottom meta tracker indicator to match live layout settings */}
          <span>Budget: {fmtMoney(totalBudget)}</span>
          <span>Transactions: {monthTotals.month_txn}</span>
        </div>
      </div>

      <div className="two-col">
        <div className="card">
          <h3>Top Categories This Month</h3>
          {cleanTopCategories.length === 0 ? <p className="empty">No expenses tracked this month</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={cleanTopCategories} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={false}>
                  {cleanTopCategories.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => fmtMoney(v)} />
                <Legend layout="horizontal" align="center" verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card">
          <h3>Recent Transactions</h3>
          <div className="txn-list">
            {recentTransactions.length === 0 ? <p className="empty">No transactions logged</p> : recentTransactions.map(t => (
              <div key={t.id} className="txn-row">
                <div className="txn-left"><span className="txn-icon">{t.icon}</span>
                  <div>
                    <div className="txn-name">{t.particulars}</div>
                    <div className="txn-meta">{t.category} · {fmtDate(t.expense_date)}</div>
                    <div className="txn-created">added {t.created_at}</div>
                  </div>
                </div>
                <span className={`txn-amount ${t.debit>0?'debit':'credit'}`}>{t.debit > 0 ? '−' : '+'}{fmtMoney(t.debit > 0 ? t.debit : t.credit)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}



function ExpensesPage({categories, onAdd, toast, onRefreshCategories}) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);
  
  // 1. Automatically default filters to the current year and current month
  const now = new Date();
  const [filter, setFilter]   = useState({
    date: '', 
    month: String(now.getMonth() + 1), // 1-indexed string matching your option keys
    year: String(now.getFullYear()), 
    type: ''
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 40;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (filter.date) p.date = filter.date;
      if (filter.year) p.year = filter.year;
      if (filter.month) p.month = filter.month;
      if (filter.type) p.type = filter.type;
      const r = await getExpenses(p);
      setRows(r.data.data || r.data || []);
    } catch (err) {
      console.error("Failed to sync records stream:", err);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Reset pagination to page 1 whenever any filter configuration gets modified
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await deleteExpense(id);
      toast('Deleted successfully', 'success');
      load();
    } catch {
      toast('Error performing delete', 'error');
    }
  };

  const setF = (k, v) => setFilter(f => ({ ...f, [k]: v }));
  const years = Array.from({length: 5}, (_, i) => new Date().getFullYear() - i);
  const months = Array.from({length: 12}, (_, i) => i + 1);

  // ── PAGINATION CHUNKING MATH ──
  const totalRecords = rows.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage) || 1;
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = rows.slice(indexOfFirstRecord, indexOfLastRecord);

  const parsePaymentLabel = (method) => {
    const m = method || 'cash';
    switch (m) {
      case 'gpay': return '📱 GPay';
      case 'phonepe': return '📱 PhonePe';
      case 'paytm': return '📱 Paytm';
      case 'upi': return '⚡ UPI';
      case 'meal_card': return '🍱 HDFC Meal';
      case 'axis_card': return '💳 Axis Card';
      case 'hdfc_card': return '💳 HDFC Card';
      case 'sbi_card': return '💳 SBI Card';
      case 'indian_bank_card': return '💳 Ind Bank Card';
      case 'credit_card': return '💳 Credit Card';
      case 'net_axis': return '🏛️ Axis Net';
      case 'net_hdfc': return '🏛️ HDFC Net';
      case 'net_sbi': return '🏛️ SBI Net';
      case 'net_ib': return '🏛️ Ind Bank Net';
      case 'net_banking': return '🌐 NetBanking';
      case 'cash':
      default:
        return '💵 Cash';
    }
  };

  // ── REUSABLE DUAL PAGINATION CONTROL DECK ──
  const renderPaginationBar = () => (
    <div className="pagination-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 5px', gap: '15px', flexWrap: 'wrap' }}>
      <span className="pagination-info" style={{ color: '#64748b', fontSize: '13px' }}>
        Showing <b>{totalRecords === 0 ? 0 : indexOfFirstRecord + 1}</b> - <b>{Math.min(indexOfLastRecord, totalRecords)}</b> of <b>{totalRecords}</b> entries
      </span>
      
      <div className="pagination-buttons" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <button 
          onClick={() => setCurrentPage(1)} 
          disabled={currentPage === 1}
          style={{ padding: '6px 12px', background: '#0f172a', border: '1px solid #334155', color: currentPage === 1 ? '#475569' : '#f8fafc', borderRadius: '4px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
        >
          « First
        </button>
        <button 
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
          disabled={currentPage === 1}
          style={{ padding: '6px 12px', background: '#0f172a', border: '1px solid #334155', color: currentPage === 1 ? '#475569' : '#f8fafc', borderRadius: '4px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
        >
          ‹ Prev
        </button>
        
        <span style={{ padding: '0 8px', color: '#a855f7', fontWeight: 600, fontSize: '14px' }}>
          Page {currentPage} of {totalPages}
        </span>

        <button 
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
          disabled={currentPage === totalPages}
          style={{ padding: '6px 12px', background: '#0f172a', border: '1px solid #334155', color: currentPage === totalPages ? '#475569' : '#f8fafc', borderRadius: '4px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
        >
          Next ›
        </button>
        <button 
          onClick={() => setCurrentPage(totalPages)} 
          disabled={currentPage === totalPages}
          style={{ padding: '6px 12px', background: '#0f172a', border: '1px solid #334155', color: currentPage === totalPages ? '#475569' : '#f8fafc', borderRadius: '4px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
        >
          Last »
        </button>
      </div>
    </div>
  );

  return (
    <div className="page expansive-layout">
      <div className="page-header">
        <h1>Expenses Ledger</h1>
        <button className="btn btn-primary" onClick={onAdd}>+ Add</button>
      </div>

      <div className="card filter-bar">
        <input type="date" value={filter.date} onChange={e => setF('date', e.target.value)}/>
        <select value={filter.year} onChange={e => setF('year', e.target.value)}><option value="">All Years</option>{years.map(y => <option key={y} value={y}>{y}</option>)}</select>
        <select value={filter.month} onChange={e => setF('month', e.target.value)}><option value="">All Months</option>{months.map(m => <option key={m} value={m}>{MONTH_NAMES[m]}</option>)}</select>
        <select value={filter.type} onChange={e => setF('type', e.target.value)}><option value="">All Types</option><option value="food">Food</option><option value="non_food">Non-Food</option></select>
        <button className="btn btn-ghost" onClick={() => setFilter({date:'', month:'', year:'', type:''})}>Clear</button>
      </div>

      {/* 🔝 TOP FLOATING PAGINATION DECK */}
      {!loading && rows.length > 0 && renderPaginationBar()}

      {loading ? <div className="loading">Loading records…</div> : (
        <div className="card full-width-table-card" style={{ marginTop: '5px' }}>
          <div className="table-scroll-viewport">
            <table className="fluid-expense-table">
              <thead>
                <tr>
                  <th>Expense Date</th>
                  <th>Created At</th>
                  <th>Particulars</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Paid Via</th>
                  <th className="num">Credit</th>
                  <th className="num">Debit</th>
                  <th>Remarks</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.length === 0 ? (
                  <tr><td colSpan={10} className="empty-cell">No records found</td></tr>
                ) : currentRecords.map(r => (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.expense_date)}</td>
                    <td style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{r.created_at}</td>
                    <td className="text-wrap-cell" style={{ fontWeight: '500' }}>{r.particulars}</td>
                    <td><span className="cat-chip">{r.category_icon} {r.category_name}</span></td>
                    <td><Badge type={r.category_type}/></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{parsePaymentLabel(r.payment_method)}</td>
                    <td className="num credit-text">{r.credit > 0 ? fmtMoney(r.credit) : '—'}</td>
                    <td className="num debit-text">{r.debit > 0 ? fmtMoney(r.debit) : '—'}</td>
                    <td className="text-wrap-cell text-muted">{r.remarks || '—'}</td>
                    <td>
                      <div className="action-btns-row">
                        <button className="table-action-btn edit" title="Edit" onClick={() => setEditRow(r)}>✏️ Edit</button>
                        <button className="table-action-btn delete" title="Delete" onClick={() => handleDelete(r.id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={6} className="foot-label">Total Current Dataset ({rows.length} records)</td>
                    <td className="num credit-text" style={{ fontWeight: '700' }}>{fmtMoney(rows.reduce((s, r) => s + parseFloat(r.credit || 0), 0))}</td>
                    <td className="num debit-text" style={{ fontWeight: '700' }}>{fmtMoney(rows.reduce((s, r) => s + parseFloat(r.debit || 0), 0))}</td>
                    <td colSpan={2}/>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* 🛑 BOTTOM FLOATING PAGINATION DECK */}
      {!loading && rows.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          {renderPaginationBar()}
        </div>
      )}

      {editRow && (
        <ExpenseForm
          categories={categories}
          editData={editRow}
          onRefreshCategories={onRefreshCategories}
          onSave={() => { setEditRow(null); load(); toast('Updated!', 'success'); }}
          onClose={() => setEditRow(null)}
        />
      )}
    </div>
  );
}
{/*

function ExpensesPage({categories, onAdd, toast, onRefreshCategories}) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [filter, setFilter]   = useState({date:'', month:'', year:'', type:''});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (filter.date) p.date = filter.date;
      if (filter.year) p.year = filter.year;
      if (filter.month) p.month = filter.month;
      if (filter.type) p.type = filter.type;
      const r = await getExpenses(p);
      setRows(r.data.data || r.data || []);
    } catch (err) {
      console.error("Failed to sync records stream:", err);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await deleteExpense(id);
      toast('Deleted successfully', 'success');
      load();
    } catch {
      toast('Error performing delete', 'error');
    }
  };
  
  const setF = (k, v) => setFilter(f => ({ ...f, [k]: v }));
  const years = Array.from({length: 5}, (_, i) => new Date().getFullYear() - i);
  const months = Array.from({length: 12}, (_, i) => i + 1);

  const parsePaymentLabel = (method) => {
    const m = method || 'cash';
    switch (m) {
      case 'gpay': return '📱 GPay';
      case 'phonepe': return '📱 PhonePe';
      case 'paytm': return '📱 Paytm';
      case 'upi': return '⚡ UPI';
      case 'meal_card': return '🍱 HDFC Meal';
      case 'axis_card': return '💳 Axis Card';
      case 'hdfc_card': return '💳 HDFC Card';
      case 'sbi_card': return '💳 SBI Card';
      case 'indian_bank_card': return '💳 Ind Bank Card';
      case 'credit_card': return '💳 Credit Card';
      case 'net_axis': return '🏛️ Axis Net';
      case 'net_hdfc': return '🏛️ HDFC Net';
      case 'net_sbi': return '🏛️ SBI Net';
      case 'net_ib': return '🏛️ Ind Bank Net';
      case 'net_banking': return '🌐 NetBanking';
      case 'cash':
      default: 
        return '💵 Cash';
    }
  };

  return (
    <div className="page expansive-layout">
      <div className="page-header">
        <h1>Expenses Ledger</h1>
        <button className="btn btn-primary" onClick={onAdd}>+ Add</button>
      </div>
      
      <div className="card filter-bar">
        <input type="date" value={filter.date} onChange={e => setF('date', e.target.value)}/>
        <select value={filter.year} onChange={e => setF('year', e.target.value)}><option value="">All Years</option>{years.map(y => <option key={y} value={y}>{y}</option>)}</select>
        <select value={filter.month} onChange={e => setF('month', e.target.value)}><option value="">All Months</option>{months.map(m => <option key={m} value={m}>{MONTH_NAMES[m]}</option>)}</select>
        <select value={filter.type} onChange={e => setF('type', e.target.value)}><option value="">All Types</option><option value="food">Food</option><option value="non_food">Non-Food</option></select>
        <button className="btn btn-ghost" onClick={() => setFilter({date:'', month:'', year:'', type:''})}>Clear</button>
      </div>
      
      {loading ? <div className="loading">Loading records…</div> : (
        <div className="card full-width-table-card">
          <div className="table-scroll-viewport">
            <table className="fluid-expense-table">
              <thead>
                <tr>
                  <th>Expense Date</th>
                  <th>Created At</th>
                  <th>Particulars</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Paid Via</th>
                  <th className="num">Credit</th>
                  <th className="num">Debit</th>
                  <th>Remarks</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={10} className="empty-cell">No records found</td></tr>
                ) : rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.expense_date)}</td>
                    <td style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{r.created_at}</td>
                    <td className="text-wrap-cell" style={{ fontWeight: '500' }}>{r.particulars}</td>
                    <td><span className="cat-chip">{r.category_icon} {r.category_name}</span></td>
                    <td><Badge type={r.category_type}/></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{parsePaymentLabel(r.payment_method)}</td>
                    <td className="num credit-text">{r.credit > 0 ? fmtMoney(r.credit) : '—'}</td>
                    <td className="num debit-text">{r.debit > 0 ? fmtMoney(r.debit) : '—'}</td>
                    <td className="text-wrap-cell text-muted">{r.remarks || '—'}</td>
                    <td>
                      <div className="action-btns-row">
                        <button className="table-action-btn edit" title="Edit" onClick={() => setEditRow(r)}>✏️ Edit</button>
                        <button className="table-action-btn delete" title="Delete" onClick={() => handleDelete(r.id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={6} className="foot-label">Total ({rows.length} records)</td>
                    <td className="num credit-text" style={{ fontWeight: '700' }}>{fmtMoney(rows.reduce((s, r) => s + parseFloat(r.credit || 0), 0))}</td>
                    <td className="num debit-text" style={{ fontWeight: '700' }}>{fmtMoney(rows.reduce((s, r) => s + parseFloat(r.debit || 0), 0))}</td>
                    <td colSpan={2}/>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {editRow && (
        <ExpenseForm 
          categories={categories} 
          editData={editRow} 
          onRefreshCategories={onRefreshCategories} 
          onSave={() => { setEditRow(null); load(); toast('Updated!', 'success'); }} 
          onClose={() => setEditRow(null)} 
        />
      )}
    </div>
  );
}
*/}

// ══════════════════════════════════════════════════════════
// MONTHLY REPORT ARCHIVE (With High-Fidelity PDF Export)
// ══════════════════════════════════════════════════════════
function MonthlySummaryPage() {
  const now=new Date();
  const [year,setYear]   = useState(now.getFullYear());
  const [month,setMonth] = useState(now.getMonth()+1);
  const [data,setData]   = useState(null);
  const [loading,setLoading]=useState(false);

  const load=useCallback(async()=>{setLoading(true);try{const r=await getMonthlySummary(year,month);setData(r.data);}catch{}setLoading(false);},[year,month]);
  useEffect(()=>{load();},[load]);

  const years=Array.from({length:5},(_,i)=>now.getFullYear()-i);
  const months=Array.from({length:12},(_,i)=>i+1);

  // Print CSS Engine injection for seamless layout exports
  const triggerNativePDFPrint = () => {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = `
      @media print {
        body { background: #ffffff !important; color: #000000 !important; font-size: 12px; }
        .mobile-header, .sidebar, .month-picker, .sidebar-overlay, button, .page-header button { display: none !important; }
        .main { margin-left: 0 !important; padding: 0 !important; width: 100% !important; background: transparent !important; }
        .page { padding: 0 !important; margin: 0 !important; background: transparent !important; }
        .card, .stat-card, table { background: #ffffff !important; border: 1px solid #cbd5e1 !important; color: #000000 !important; box-shadow: none !important; margin-bottom: 12px !important; page-break-inside: avoid; }
        .stat-value, h1, h2, h3, th, td, span, div { color: #000000 !important; }
        .progress-bar-track, .mini-bar-track { background: #e2e8f0 !important; border: 1px solid #94a3b8 !important; }
        .progress-bar-fill { background: #475569 !important; }
        th { background: #f1f5f9 !important; border-bottom: 2px solid #cbd5e1 !important; }
        td { border-bottom: 1px solid #e2e8f0 !important; }
        footer { display: block !important; position: running(footer); color: #000000 !important; border-top: 1px solid #000000 !important; }
      }
    `;
    document.head.appendChild(styleTag);
    window.print();
    setTimeout(() => { document.head.removeChild(styleTag); }, 1000);
  };

  // Safe round graph data adapter mapping (Fixed Overlapping Text)
  const parsedPieData = data?.categories
    ? data.categories
        .filter(c => parseFloat(c.total_debit || 0) > 0)
        .map(c => ({
          name: `${c.icon || ''} ${c.category_name || ''}`.trim(),
          value: parseFloat(c.total_debit || 0)
        }))
    : [];

  return (
    <div className="page">
      <div className="page-header">
        <h1>Monthly Report</h1>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={triggerNativePDFPrint} style={{ background: '#10b981', fontWeight: 600 }}>
            📥 Save PDF Report
          </button>
          <div className="month-picker">
            <select value={month} onChange={e=>setMonth(+e.target.value)}>{months.map(m=><option key={m} value={m}>{MONTH_NAMES[m]}</option>)}</select>
            <select value={year} onChange={e=>setYear(+e.target.value)}>{years.map(y=><option key={y} value={y}>{y}</option>)}</select>
          </div>
        </div>
      </div>

      {loading ? <div className="loading">Generating metrics…</div> : !data ? null : (
        <div id="printable-report-area">
          <div className="stat-grid">
            <div className="stat-card stat-budget"><div className="stat-icon">💰</div><div className="stat-info"><span className="stat-label">Budget</span><span className="stat-value">{fmtMoney(data.budget)}</span></div></div>
            <div className="stat-card stat-credit"><div className="stat-icon">📥</div><div className="stat-info"><span className="stat-label">Total Credit</span><span className="stat-value">{fmtMoney(data.totals.grand_credit)}</span></div></div>
            <div className="stat-card stat-debit"><div className="stat-icon">📤</div><div className="stat-info"><span className="stat-label">Total Spent</span><span className="stat-value">{fmtMoney(data.totals.grand_debit)}</span></div></div>
            <div className={`stat-card ${data.totals.net_savings>=0?'stat-credit':'stat-danger'}`}><div className="stat-icon">{data.totals.net_savings>=0?'🏦':'⚠️'}</div><div className="stat-info"><span className="stat-label">Net Savings</span><span className="stat-value">{fmtMoney(data.totals.net_savings)}</span></div></div>
          </div>
          <div className="card">
            <h3>Budget Validation — {MONTH_NAMES[month]} {year}</h3>
            <div className="progress-bar-wrap"><div className="progress-bar-track"><div className={`progress-bar-fill ${pct(data.totals.grand_debit,data.budget)>90?'fill-danger':pct(data.totals.grand_debit,data.budget)>70?'fill-warn':'fill-ok'}`} style={{width:`${pct(data.totals.grand_debit,data.budget)}%`}}/></div><span className="progress-label">{pct(data.totals.grand_debit,data.budget)}%</span></div>
            <div className="budget-row"><span>Spent: {fmtMoney(data.totals.grand_debit)}</span><span>Remaining: {fmtMoney(data.budget-data.totals.grand_debit)}</span><span>Transactions: {data.totals.total_transactions}</span></div>
          </div>
          <div className="two-col">
            <div className="card"><h3>Daily Spending Trend</h3>
              {data.daily.length===0?<p className="empty">No layout parameters logged</p>:(
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.daily} margin={{top:5,right:10,left:0,bottom:5}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155"/>
                    <XAxis dataKey="expense_date" tick={{fontSize:10}} tickFormatter={d=>d.slice(8)}/>
                    <YAxis tick={{fontSize:10}}/><Tooltip formatter={v=>fmtMoney(v)}/><Legend/>
                    <Bar dataKey="debit" name="Spent" fill="#ef4444" radius={[3,3,0,0]}/>
                    <Bar dataKey="credit" name="Credit" fill="#10b981" radius={[3,3,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="card"><h3>Spend by Category</h3>
              {parsedPieData.length===0?<p className="empty">No categorical spend logged</p>:(
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={parsedPieData} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={100} label={false}>
                      {parsedPieData.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                    </Pie>
                    <Tooltip formatter={v=>fmtMoney(v)}/>
                    <Legend layout="horizontal" align="center" verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: '11px', maxHeight: '60px', overflowY: 'auto' }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="card table-card"><h3>Category Breakdown</h3>
            <table className="expense-table">
              <thead><tr><th>Category</th><th>Type</th><th className="num">Transactions</th><th className="num">Credit</th><th className="num">Debit</th><th className="num">% of Budget</th></tr></thead>
              <tbody>
                {data.categories.length===0?<tr><td colSpan={6} className="empty-cell">No details parsed</td></tr>:data.categories.map((c,i)=>(
                  <tr key={i}><td><span className="cat-chip">{c.icon} {c.category_name}</span></td><td><Badge type={c.category_type}/></td><td className="num">{c.transactions}</td><td className="num credit-text">{fmtMoney(c.total_credit)}</td><td className="num debit-text">{fmtMoney(c.total_debit)}</td>
                    <td className="num"><div className="mini-bar-wrap"><div className="mini-bar-track"><div className="mini-bar-fill" style={{width:`${pct(c.total_debit,data.budget)}%`}}/></div><span>{pct(c.total_debit,data.budget)}%</span></div></td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td colSpan={3} className="foot-label">Grand Total</td><td className="num credit-text">{fmtMoney(data.totals.grand_credit)}</td><td className="num debit-text">{fmtMoney(data.totals.grand_debit)}</td><td className="num">{pct(data.totals.grand_debit,data.budget)}%</td></tr></tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// EXPENSE ANALYSIS DASHBOARD (Category-wise Tracking)
// ══════════════════════════════════════════════════════════
function ExpenseAnalysisPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getMonthlySummary(year, month);
      setData(r.data);
    } catch (err) {
      console.error("Error loading analysis data:", err);
    }
    setLoading(false);
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  if (loading) return <div className="loading">Analyzing compilation data…</div>;
  if (!data) return <div className="loading">No summary ledger data found.</div>;

  const debitChartData = data.categories
    .filter(c => parseFloat(c.total_debit || 0) > 0)
    .map(c => ({
      name: `${c.icon} ${c.category_name}`,
      Amount: parseFloat(c.total_debit || 0)
    }));

  const creditChartData = data.categories
    .filter(c => parseFloat(c.total_credit || 0) > 0)
    .map(c => ({
      name: `${c.icon} ${c.category_name}`,
      Amount: parseFloat(c.total_credit || 0)
    }));

  let cumulativeSum = 0;
  const waveChartData = data.daily.map(d => {
    cumulativeSum += parseFloat(d.debit || 0);
    return {
      date: d.expense_date.slice(8),
      "Daily Spent": parseFloat(d.debit || 0),
      "Cumulative Run": cumulativeSum
    };
  });

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>📊 Expense & Credit Analysis</h1>
          <p className="subtitle">Detailed categorical distribution, income tracking & budget metrics</p>
        </div>
        <div className="month-picker">
          <select value={month} onChange={e => setMonth(+e.target.value)}>
            {months.map(m => <option key={m} value={m}>{MONTH_NAMES[m]}</option>)}
          </select>
          <select value={year} onChange={e => setYear(+e.target.value)}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Visual Analytics Block Placed First */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '24px' }}>
        
        {/* Row 1: Side-by-Side Bar Charts */}
        <div className="two-col">
          <div className="card">
            <h3 style={{ color: '#f97316' }}>🔴 Expenses Breakdown (Debits)</h3>
            {debitChartData.length === 0 ? (
              <p className="empty">No expense entries to visualize</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={debitChartData} margin={{ top: 20, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155' }} />
                  <Bar dataKey="Amount" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="card">
            <h3 style={{ color: '#10b981' }}>🟢 Income Breakdown (Credits)</h3>
            {creditChartData.length === 0 ? (
              <p className="empty">No cash inflow or credits logged this month</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={creditChartData} margin={{ top: 20, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155' }} />
                  <Bar dataKey="Amount" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Row 2: Wave Chart */}
        <div className="card">
          <h3>Spending Velocity Trend (Wave Chart)</h3>
          {waveChartData.length === 0 ? (
            <p className="empty">No sequential ledger metrics active</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={waveChartData} margin={{ top: 20, right: 10, left: -10, bottom: 5 }}>
                <defs>
                  <linearGradient id="waveColor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} label={{ value: 'Day of Month', position: 'insideBottom', offset: -5, fill: '#64748b', fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155' }} />
                <Legend verticalAlign="top" height={36}/>
                <Area type="monotone" dataKey="Cumulative Run" stroke="#06b6d4" strokeWidth={3} fillOpacity={1} fill="url(#waveColor)" name="Cumulative Total" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Category Breakdown Positioned Next/Below Graphs */}
      <div className="card">
        <h3>Category Breakdown</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {data.categories.length === 0 ? (
            <p className="empty">No categorical records logged this month</p>
          ) : (
            data.categories.map((c, i) => {
              const isCredit = parseFloat(c.total_credit) > 0;
              const amount = isCredit ? c.total_credit : c.total_debit;
              const spentPct = pct(amount, data.budget);

              return (
                <div key={i} style={{ background: '#1e293b', padding: '12px 16px', borderRadius: '8px', borderLeft: isCredit ? '4px solid #10b981' : '4px solid #ef4444' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600 }}>
                      {c.icon} {c.category_name}{' '}
                      <span style={{ fontSize: '11px', fontWeight: 400, color: '#94a3b8' }}>
                        ({c.transactions} txns · {isCredit ? '🟢 Credit' : '🔴 Debit'})
                      </span>
                    </span>
                    <span style={{ fontWeight: 700, color: isCredit ? '#10b981' : '#ef4444' }}>
                      {isCredit ? '+' : '-'}{fmtMoney(amount)}
                      {!isCredit && <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 400 }}> ({spentPct}%)</span>}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function CashFlowAnalysisPage() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initialize filter state tracking using the current year and month
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

  useEffect(() => {
    getExpenses()
      .then(res => {
        // ── BULLETPROOF API UNPACKER ──
        const cleanData = res.data?.data ? res.data.data : (res.data ? res.data : res);
        if (Array.isArray(cleanData)) {
          setExpenses(cleanData);
        }
      })
      .catch(err => console.error("Analysis data fetch failed:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Processing Cash Flow Metrics...</div>;

  // ── DYNAMIC MONTHLY FILTRATION CRITERIA ──
  const filteredExpenses = expenses.filter(t => {
    if (!t.expense_date) return false;
    const rowDate = new Date(t.expense_date);
    return rowDate.getFullYear() === parseInt(selectedYear) && (rowDate.getMonth() + 1) === parseInt(selectedMonth);
  });

  // ── GENERALIZED PUBLIC METHOD PARSING ──
// ── REVISED PUBLIC METHOD PARSING ──
const processRowMode = (t) => {
  // Normalize the input string safely to lowercase
  const method = (t.payment_method || 'cash').toLowerCase().trim();
  
  // ✅ Broad public matchers that handle emojis and common wallet names
  if (
    method.includes('upi') || 
    method.includes('gpay') || 
    method.includes('paytm') || 
    method.includes('phonepe') || 
    method.includes('digital') || 
    method.includes('wallet')
  ) {
    return 'upi';
  }
  
  if (
    method.includes('card') || 
    method.includes('debit') || 
    method.includes('credit') ||
    method.includes('axis') ||
    method.includes('hdfc') ||
    method.includes('icici') ||
    method.includes('sbi') ||
    method.includes('kotak') ||
    method.includes('ib') ||
    method.includes('iob') 
  ) {
    return 'card';
  }
  
  return 'cash';
};
  // ── 1. Calculate Distribution Pools based on filtered dataset ──
  const distribution = filteredExpenses.reduce((acc, t) => {
    const debitVal = parseFloat(t.debit || 0);
    if (debitVal <= 0) return acc;

    const mode = processRowMode(t);
    if (mode === 'cash') {
      acc.cashTotal += debitVal;
    } else if (mode === 'card') {
      acc.cardTotal += debitVal;
    } else {
      acc.upiTotal += debitVal;
    }
    return acc;
  }, { cashTotal: 0, cardTotal: 0, upiTotal: 0 });

  const pieChartData = [
    { name: 'Liquid Cash', value: distribution.cashTotal, color: '#f97316' },
    { name: 'Card Transactions', value: distribution.cardTotal, color: '#06b6d4' },
    { name: 'UPI & Digital Transfers', value: distribution.upiTotal, color: '#a855f7' }
  ].filter(d => d.value > 0);

  // ── 2. Calculate Continuous Time Series based on filtered dataset ──
  const timelineMap = filteredExpenses.reduce((acc, t) => {
    const debitVal = parseFloat(t.debit || 0);
    if (debitVal <= 0) return acc;

    const dateStr = t.expense_date;
    if (!acc[dateStr]) {
      acc[dateStr] = { date: dateStr, 'Liquid Cash': 0, 'Cards': 0, 'UPI / Digital': 0 };
    }

    const mode = processRowMode(t);
    if (mode === 'cash') {
      acc[dateStr]['Liquid Cash'] += debitVal;
    } else if (mode === 'card') {
      acc[dateStr]['Cards'] += debitVal;
    } else {
      acc[dateStr]['UPI / Digital'] += debitVal;
    }
    return acc;
  }, {});

  const waveTimelineData = Object.values(timelineMap).sort((a, b) => new Date(a.date) - new Date(b.date));

  // Generate selection lists dynamically for your dropdown filters
  const yearsList = Array.from(new Set(expenses.map(e => new Date(e.expense_date).getFullYear()))).sort((a,b)=>b-a);
  if(yearsList.length === 0) yearsList.push(now.getFullYear());

  return (
    <div className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h1>💳 Cash Flow Analysis</h1>
          <p className="subtitle">Real-time metrics distributed by configured asset pools</p>
        </div>

        {/* ── CLIENT SIDE DRILLDOWN CONTROL FILTERS ── */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={{ padding: '8px 12px', background: '#0f172a', color: '#f8fafc', border: '1px solid #334155', borderRadius: '6px', cursor: 'pointer' }}
          >
            {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((m, idx) => (
              <option key={idx} value={idx + 1}>{m}</option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{ padding: '8px 12px', background: '#0f172a', color: '#f8fafc', border: '1px solid #334155', borderRadius: '6px', cursor: 'pointer' }}
          >
            {yearsList.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card" style={{borderLeft: '4px solid #f97316'}}>
          <div className="stat-info">
            <span className="stat-label">💵 Liquid Cash</span>
            <span className="stat-value" style={{color: '#f97316'}}>{fmtMoney(distribution.cashTotal)}</span>
          </div>
        </div>
        <div className="stat-card" style={{borderLeft: '4px solid #06b6d4'}}>
          <div className="stat-info">
            <span className="stat-label">💳 Card Payments</span>
            <span className="stat-value" style={{color: '#06b6d4'}}>{fmtMoney(distribution.cardTotal)}</span>
          </div>
        </div>
        <div className="stat-card" style={{borderLeft: '4px solid #a855f7'}}>
          <div className="stat-info">
            <span className="stat-label">📱 UPI & Digital transfers</span>
            <span className="stat-value" style={{color: '#a855f7'}}>{fmtMoney(distribution.upiTotal)}</span>
          </div>
        </div>
      </div>

      <div className="card" style={{marginTop: '20px'}}>
        <h3>📈 Waveform Velocity (Daily Volume Stream Over Time)</h3>
        <p className="subtitle" style={{marginBottom: '16px'}}>Visualizes liquidity release fluctuations across active channels</p>
        <div style={{width: '100%', height: 260}}>
          {waveTimelineData.length === 0 ? <p className="empty">No layout parameters to build time series for this month</p> : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={waveTimelineData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorCash" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f97316" stopOpacity={0.4}/><stop offset="95%" stopColor="#f97316" stopOpacity={0}/></linearGradient>
                  <linearGradient id="colorCard" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4}/><stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/></linearGradient>
                  <linearGradient id="colorUPI" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#a855f7" stopOpacity={0.4}/><stop offset="95%" stopColor="#a855f7" stopOpacity={0}/></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="#64748b" style={{fontSize: 11}} tickFormatter={d => d.slice(8)} />
                <YAxis stroke="#64748b" style={{fontSize: 11}} />
                <Tooltip contentStyle={{background: '#0f172a', borderColor: '#334155'}} formatter={v => fmtMoney(v)} />
                <Area type="monotone" dataKey="Liquid Cash" stroke="#f97316" strokeWidth={2} fillOpacity={1} fill="url(#colorCash)" />
                <Area type="monotone" dataKey="Cards" stroke="#06b6d4" strokeWidth={2} fillOpacity={1} fill="url(#colorCard)" />
                <Area type="monotone" dataKey="UPI / Digital" stroke="#a855f7" strokeWidth={2} fillOpacity={1} fill="url(#colorUPI)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="two-col" style={{marginTop: '20px'}}>
        <div className="card">
          <h3>📊 Pool Share Allocation Ratio</h3>
          <div style={{width: '100%', height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            {pieChartData.length === 0 ? <p className="empty">No structural records saved for this month</p> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={85} paddingAngle={3}>
                    {pieChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={v => fmtMoney(v)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card">
          <h3>📋 Payment Mode Flow Matrix</h3>
          <div className="table-card" style={{boxShadow:'none', padding:0}}>
            <table className="expense-table" style={{marginTop: '8px'}}>
              <thead>
                <tr>
                  <th>Target Stream</th>
                  <th className="num">Outflow Value</th>
                  <th>Share Ratio</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const gross = (distribution.cashTotal + distribution.cardTotal + distribution.upiTotal) || 1;
                  return (
                    <>
                      <tr>
                        <td><span className="bullet-dot" style={{background: '#f97316'}}></span> 💵 Liquid Cash</td>
                        <td className="num debit-text" style={{fontWeight:600}}>{fmtMoney(distribution.cashTotal)}</td>
                        <td><span className="cat-chip" style={{background:'rgba(249,115,22,0.15)', color:'#f97316'}}>{((distribution.cashTotal / gross) * 100).toFixed(1)}%</span></td>
                      </tr>
                      <tr>
                        <td><span className="bullet-dot" style={{background: '#06b6d4'}}></span> 💳 Card Payments</td>
                        <td className="num debit-text" style={{fontWeight:600}}>{fmtMoney(distribution.cardTotal)}</td>
                        <td><span className="cat-chip" style={{background:'rgba(6,182,212,0.15)', color:'#06b6d4'}}>{((distribution.cardTotal / gross) * 100).toFixed(1)}%</span></td>
                      </tr>
                      <tr>
                        <td><span className="bullet-dot" style={{background: '#a855f7'}}></span> 📱 UPI & Digital Transfers</td>
                        <td className="num debit-text" style={{fontWeight:600}}>{fmtMoney(distribution.upiTotal)}</td>
                        <td><span className="cat-chip" style={{background:'rgba(168,85,247,0.15)', color:'#a855f7'}}>{((distribution.upiTotal / gross) * 100).toFixed(1)}%</span></td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ENTRY MAIN APPLICATION CONTAINER
// ══════════════════════════════════════════════════════════
export default function App() {
  const [user,       setUser]       = useState(()=>getStored('user'));
  const [page,       setPage]       = useState('dashboard');
  const [showForm,   setShowForm]   = useState(false);
  const [categories, setCategories] = useState([]);
  const [toast,      setToast]      = useState(null);
  const [refresh,    setRefresh]    = useState(0);
  const [menuOpen,   setMenuOpen]   = useState(false);

const [showBudgetControls, setShowBudgetControls] = useState(false);
// const [newBaseBudget, setNewBaseBudget] = useState(user?.monthly_budget || 14000);
	const [newBaseBudget, setNewBaseBudget] = useState(user?.monthly_budget || 0);
const [emergencyTopup, setEmergencyTopup] = useState('');
const [budgetLoading, setBudgetLoading] = useState(false);
const [view, setView] = useState('dashboard'); // 'dashboard', 'expenses', 'reports', 'analysis'

  const fetchCategoriesList = useCallback(() => {
    if(user) getCategories().then(r=>setCategories(r.data.data)).catch(()=>{});
  }, [user]);

  useEffect(()=>{
    fetchCategoriesList();
  },[fetchCategoriesList]);

  const navigateTo = (pageId) => {
    setPage(pageId);
    setMenuOpen(false);
  };

  const showToast=(msg,type='success')=>{ setToast({msg,type}); setTimeout(()=>setToast(null),3000); };
  const handleAuth=(u)=>{ setUser(u); setPage('dashboard'); };

  const handleLogout=()=>{
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setPage('dashboard');
    setMenuOpen(false);
  };

  if(!user) return <AuthPage onAuth={handleAuth}/>;

	// 📑 Inside App.js -> handleSidebarBudgetUpdate
const handleSidebarBudgetUpdate = async () => {
  setBudgetLoading(true);
  try {
    const activeToken = localStorage.getItem('token');

    if (!activeToken) {
      alert("❌ Session expired. Please log out and log back in.");
      return;
    }

    // Hit the newly corrected endpoint address
    const res = await axios.post('/api/auth/update-budget', {
      monthly_budget: parseFloat(newBaseBudget) || 0,
      add_topup: parseFloat(emergencyTopup) || 0
    }, {
      headers: { Authorization: `Bearer ${activeToken}` } // Pass validation headers cleanly
    });
// 📑 Update the success block inside App.js -> handleSidebarBudgetUpdate
if (res.data?.success) {
  if (res.data.token) {
    // 1. Instantly overwrite the local storage pointer
    localStorage.setItem('token', res.data.token);
    
    // 2. Safely sync your global state hooks if they exist in your component root
    if (typeof setToken === 'function') setToken(res.data.token);
    if (typeof setUser === 'function' && res.data.user) setUser(res.data.user);
  }
  
  // 3. 🔥 THE REAL-TIME FIX: Look near the top of App.js for your initial load 
  // function (often named initApp, fetchInitialData, or loadUserData) and call it here:
  if (typeof initApp === 'function') {
    await initApp(); 
  } else if (typeof fetchDashboardData === 'function') {
    // Or if you found your metric calculation hook, fire it instead:
    await fetchDashboardData();
  }

  alert('🎯 Monthly budget distribution synchronized successfully!');
  
}
  } catch (err) {
    console.error('Failed updating budget:', err);
    alert('❌ Error updating monthly budget profiles.');
  } finally {
    setBudgetLoading(false);
  }
};

  const nav=[
    {id:'dashboard',label:'Dashboard',icon:'🏠'},
    {id:'expenses', label:'Expenses', icon:'📋'},
    {id:'summary',  label:'Monthly Report',icon:'📊'},
    {id:'analysis', label:'Expense Analysis',icon:'📈'},
	  { id: 'cashflow',  label: 'Cash Flow Analysis', icon: '💳' }
  ];
const handleNavClick = (pageId) => {
    navigateTo(pageId);
    setMenuOpen(false);
  };
return (
    <div className="app">
      <header className="mobile-header">
        <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? '✕' : '☰'}
        </button>
        <div className="mobile-brand">
          <span className="brand-icon">💸</span>
          <span className="brand-name">ExpenseTracker</span>
        </div>
        <button className="mobile-add-btn" onClick={() => setShowForm(true)}>+</button>
      </header>

      {menuOpen && <div className="sidebar-overlay" onClick={() => setMenuOpen(false)} />}

      <aside className={`sidebar ${menuOpen ? 'open' : 'closed-mobile'}`}>
        <div className="brand">
          <span className="brand-icon">💸</span>
          <div>
            <div className="brand-name">ExpenseTracker</div>
            <div className="brand-sub">Budget: {Number(user.monthly_budget || 14000).toLocaleString('en-IN')}/mo</div>
          </div>
        </div>
        <div className="user-pill">
          <div className="user-avatar">{user.name?.[0]?.toUpperCase()}</div>
          <div className="user-info"><div className="user-name">{user.name}</div><div className="user-email">{user.email}</div></div>
        </div>
        <nav className="nav">
          {nav.map(n => (
            <button key={n.id} className={`nav-item ${page === n.id ? 'active' : ''}`} onClick={() => handleNavClick(n.id)}>
              <span className="nav-icon">{n.icon}</span><span>{n.label}</span>
            </button>
          ))}
        </nav>

        {/* 🔥 COLLAPSIBLE SIDEBAR BUDGET CONTROLLER WIDGET */}
        <div style={{ padding: '0 16px', marginTop: '8px', marginBottom: '8px' }}>
          <button
            onClick={() => setShowBudgetControls(!showBudgetControls)}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 12px',
              backgroundColor: showBudgetControls ? '#1e293b' : 'transparent',
              color: showBudgetControls ? '#f97316' : '#94a3b8',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              textAlign: 'left'
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>⚙️ Budget Settings</span>
            <span>{showBudgetControls ? '▲' : '▼'}</span>
          </button>

	{/*
	{showBudgetControls && (
            <div style={{
              backgroundColor: '#1e293b',
              padding: '12px',
              borderRadius: '0 0 8px 8px',
              marginTop: '2px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              border: '1px solid #334155',
              borderTop: 'none'
            }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>
                  Base Budget (₹)
                </label>
                <input
                  type="number"
                  value={newBaseBudget}
                  onChange={e => setNewBaseBudget(e.target.value)}
                  style={{
                    width: '100%', padding: '6px 8px', borderRadius: '4px',
                    backgroundColor: '#0f172a', border: '1px solid #334155',
                    color: '#fff', fontSize: '12px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '11px', color: '#c084fc', marginBottom: '4px', fontWeight: 'bold' }}>
                  ⚡ Add Extra Top-up (₹)
                </label>
                <input
                  type="number"
                  placeholder="e.g. 2000"
                  value={emergencyTopup}
                  onChange={e => setEmergencyTopup(e.target.value)}
                  style={{
                    width: '100%', padding: '6px 8px', borderRadius: '4px',
                    backgroundColor: '#0f172a', border: '1px solid #4c1d95',
                    color: '#fff', fontSize: '12px'
                }}
                />
                {user.budget_topup > 0 && (
                  <span style={{ fontSize: '10px', color: '#a78bfa', marginTop: '3px', display: 'block' }}>
                    Current top-up active: ₹{user.budget_topup}
                  </span>
                )}
              </div>

              <button
                onClick={handleSidebarBudgetUpdate}
                disabled={budgetLoading}
                style={{
                  width: '100%', padding: '6px', borderRadius: '4px', border: 'none',
                  backgroundColor: '#f97316', color: '#fff', fontWeight: 'bold',
                  cursor: 'pointer', fontSize: '12px', marginTop: '2px'
                }}
              >
                {budgetLoading ? 'Saving...' : 'Apply Limits ✓'}
              </button>
            </div>
          )}
*/}
{showBudgetControls && (
  <div style={{
    backgroundColor: '#1e293b',
    padding: '12px',
    borderRadius: '0 0 8px 8px',
    marginTop: '2px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    border: '1px solid #334155',
    borderTop: 'none'
  }}>
    {/* Explicit Section 1: Edit Monthly Budget */}
    <div>
      <label style={{ display: 'block', fontSize: '11px', color: '#f97316', marginBottom: '4px', fontWeight: 'bold' }}>
        ✏️ Edit Monthly Budget (Debit Cash)
      </label>
      <input
        type="number"
        value={newBaseBudget}
        onChange={e => setNewBaseBudget(e.target.value)}
        placeholder="e.g. 16000"
        style={{
          width: '100%', padding: '6px 8px', borderRadius: '4px',
          backgroundColor: '#0f172a', border: '1px solid #334155',
          color: '#fff', fontSize: '12px'
        }}
      />
    </div>

    {/* Explicit Section 2: Add Monthly Budget / Top-up */}
    <div>
      <label style={{ display: 'block', fontSize: '11px', color: '#c084fc', marginBottom: '4px', fontWeight: 'bold' }}>
        ➕ Add Monthly Budget (HDFC Meal Card)
      </label>
      <input
        type="number"
        placeholder="e.g. 16000"
        value={emergencyTopup}
        onChange={e => setEmergencyTopup(e.target.value)}
        style={{
          width: '100%', padding: '6px 8px', borderRadius: '4px',
          backgroundColor: '#0f172a', border: '1px solid #4c1d95',
          color: '#fff', fontSize: '12px'
        }}
      />
      {user.budget_topup > 0 && (
        <span style={{ fontSize: '10px', color: '#a78bfa', marginTop: '3px', display: 'block' }}>
          Active Top-up Allocation: ₹{user.budget_topup}
        </span>
      )}
    </div>

    <button
      onClick={handleSidebarBudgetUpdate}
      disabled={budgetLoading}
      style={{
        width: '100%', padding: '6px', borderRadius: '4px', border: 'none',
        backgroundColor: '#f97316', color: '#fff', fontWeight: 'bold',
        cursor: 'pointer', fontSize: '12px', marginTop: '4px'
      }}
    >
      {budgetLoading ? 'Saving...' : 'Apply Limits ✓'}
    </button>
  </div>
)}

        </div>

        <div className="sidebar-footer">
          <button className="btn btn-primary full-btn" onClick={() => { setShowForm(true); setMenuOpen(false); }}>+ New Expense</button>
          <button className="btn btn-ghost full-btn" style={{ marginTop: 8 }} onClick={handleLogout}>🚪 Sign Out</button>
        </div>
      </aside>

      <main className={`main ${menuOpen ? 'blur-background' : ''}`}>
        <div style={{ minHeight: 'calc(100vh - 120px)', paddingBottom: '30px' }}>
          {page === 'dashboard' && <Dashboard key={refresh} onAddExpense={() => setShowForm(true)} user={user} />}
          {page === 'expenses'  && <ExpensesPage key={refresh} categories={categories} onRefreshCategories={fetchCategoriesList} onAdd={() => setShowForm(true)} toast={showToast} />}
          {page === 'summary'   && <MonthlySummaryPage key={refresh} />}
          {page === 'analysis'  && <ExpenseAnalysisPage key={refresh} />}
          {page === 'cashflow'  && <CashFlowAnalysisPage key={refresh} />} {/* 🔥 Renders your new custom Cashflow Page */}
        </div>
<footer className="footer" style={{ textAlign: 'center', padding: '20px 15px', marginTop: '40px', borderTop: '1px solid #1e293b' }}>
  <p className="subtitle" style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
    © {new Date().getFullYear()} ExpenseTracker App. All Rights Reserved.
  </p>
  <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#94a3b8', fontWeight: 500 }}>
    ⚡ Powered by <span style={{ color: '#a855f7' }}>Krishnakumar Pethiah</span>
  </p>
</footer>
	{/*
        <footer style={{
          textAlign: 'center',
          padding: '20px 10px',
          borderTop: '1px solid #334155',
          color: '#64748b',
          fontSize: '13px',
          background: '#0f172a'
        }}>
          <div>© {new Date().getFullYear()} ExpenseTracker App. All Rights Reserved.</div>
          <div style={{ fontSize: '11px', marginTop: '4px', color: '#475569' }}>Personal Budget Ledger Matrix System</div>
        </footer> */}
      </main>

      {showForm&&<ExpenseForm categories={categories} editData={null} onRefreshCategories={fetchCategoriesList} onSave={()=>{setShowForm(false);setRefresh(r=>r+1);showToast('Expense added!');}} onClose={()=>setShowForm(false)}/>}
      {toast&&<Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );
}
